/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';

// Lib
import { apiClient } from '@lib/api-client';
import {
	queryClient,
	TMutationConfig,
} from '@lib/react-query';

// I18n
import { useTranslation } from 'react-i18next';

// Types
import { TApiError } from '@global-types/api/api';
import {
	TApiResponseUpdateMeterPoint,
	TBodyUpdateMeterPoint,
} from '../types';

// Constants
import { queryKeys } from '@config/constants/query-keys';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */

// Update site
export const updateMeterpoint = (args: {
	id: string;
	body: Partial<TBodyUpdateMeterPoint>;
}): Promise<TApiResponseUpdateMeterPoint> => {
	const { id, body } = args;
	return apiClient.patch(`/meter_points/${id}`, body);
};

/* -------------------------------------------------------------------------- */
/*                                   Hooks                                    */
/* -------------------------------------------------------------------------- */
export const useUpdateMeterPoint = (args: {
	page: number;
	size: number;
	mutationConfig?: TMutationConfig<
		typeof updateMeterpoint
	>;
}) => {
	const { page, size, mutationConfig } = args;
	const { t } = useTranslation();

	const { onSuccess } = mutationConfig || {};

	return useMutation({
		mutationFn: async (args: {
			id: string;
			body: Partial<TBodyUpdateMeterPoint>;
		}) => {
			return updateMeterpoint(args);
		},

		onSuccess: async (data, ...args) => {
			notifications.show({
				title: t('global.success'),
				message: t(
					'meterPoints.notifications.meterPointSuccessfullyUpdated'
				),
				color: 'var(--mantine-color-igluGreen-9)',
			});
			queryClient.invalidateQueries({
				queryKey: [
					queryKeys.app.meterPoints.list.key,
					{ page: page, size: size },
				],
			});

			onSuccess?.(data, ...args);
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'var(--mantine-color-igluRed-9)',
			});
		},
	});
};
